import { CircleAlert, LoaderCircle, RotateCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useTheme } from "@/theme/theme";
import { cn } from "@/utils/tailwind";

import { withTimeout } from "./with-timeout";

const X_WIDGET_SCRIPT_ID = "twitter-wjs";
const X_WIDGET_SCRIPT_URL = "https://platform.x.com/widgets.js";
const X_WIDGET_TIMEOUT_MS = 12_000;
const X_POST_RENDER_TIMEOUT_MS = 10_000;

type XEmbedState = "failed" | "loading" | "ready";
type XReadyCallback = (platform: XPlatform) => void;

interface XPostEmbedProps {
  fallbackTitle: string;
  fallbackUrl?: string;
  postId: string;
}

interface XEmbedOptions {
  conversation: "none";
  dnt: true;
  theme: "dark" | "light";
}

interface XWidgets {
  createTweet: (
    postId: string,
    target: HTMLElement,
    options: XEmbedOptions,
  ) => Promise<HTMLElement | undefined>;
}

interface XPlatform {
  _e?: XReadyCallback[];
  ready: (callback: XReadyCallback) => void;
  widgets?: XWidgets;
}

declare global {
  interface Window {
    twttr?: XPlatform;
  }
}

let xWidgetsPromise: Promise<XWidgets> | null = null;

// Render X's supported widget near the viewport. X controls the displayed
// length; a cached provider preview may help when the widget cannot load.
//
export function XPostEmbed({
  fallbackTitle,
  fallbackUrl,
  postId,
}: XPostEmbedProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [fallbackText, setFallbackText] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme() ?? "light";
  const [embedState, setEmbedState] = useState<XEmbedState>("loading");

  // Defer third-party script and fallback requests until a card is nearby.
  //
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (!("IntersectionObserver" in window)) {
      setNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // Keep provider fallback text out of the rich widget container and cancel it
  // when navigating away. React escapes this plain text before displaying it.
  //
  useEffect(() => {
    setFallbackText(null);
    if (!nearViewport || !fallbackUrl) return;
    const controller = new AbortController();
    void fetch(fallbackUrl, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) return;
        const data: unknown = await response.json();
        if (
          !controller.signal.aborted &&
          data &&
          typeof data === "object" &&
          "text" in data &&
          typeof data.text === "string"
        )
          setFallbackText(data.text);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [nearViewport, fallbackUrl, attempt]);

  // Recreate the provider iframe whenever the post or site theme changes.
  // This also keeps the server render provider-free until hydration.
  //
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !nearViewport) return;

    let isCancelled = false;
    const embedTarget = document.createElement("div");
    embedTarget.className = "w-full";
    container.replaceChildren(embedTarget);
    setEmbedState("loading");

    const embedPromise = loadXWidgets().then((widgets) =>
      isCancelled
        ? undefined
        : widgets.createTweet(postId, embedTarget, {
            conversation: "none",
            dnt: true,
            theme,
          }),
    );

    void withTimeout(
      embedPromise,
      X_WIDGET_TIMEOUT_MS + X_POST_RENDER_TIMEOUT_MS,
      "X did not resolve this post in time.",
    )
      .then((embedElement) => {
        if (isCancelled) return;
        if (!embedElement) {
          container.replaceChildren();
          setEmbedState("failed");
          return;
        }
        setEmbedState("ready");
      })
      .catch(() => {
        if (isCancelled) return;
        container.replaceChildren();
        setEmbedState("failed");
      });

    return () => {
      isCancelled = true;
      container.replaceChildren();
    };
  }, [postId, theme, nearViewport, attempt]);

  return (
    <div ref={rootRef} className="relative">
      <div
        ref={containerRef}
        className={cn(
          "x-post-embed mx-auto flex w-full justify-center",
          embedState === "loading" &&
            "pointer-events-none invisible absolute inset-x-0 top-0",
          embedState === "failed" && "hidden",
        )}
      />
      {embedState !== "ready" && (
        <div
          aria-live="polite"
          className="bg-background min-h-48 rounded-xl border border-dashed p-5"
          role="status"
        >
          {fallbackText ? (
            <>
              <p className="text-muted-foreground mb-3 text-xs">
                X-provided preview · long posts may be shortened
              </p>
              <blockquote className="text-sm leading-6 break-words whitespace-pre-wrap">
                {fallbackText}
              </blockquote>
              <a
                className="mt-3 inline-block text-xs underline"
                href={`https://x.com/i/web/status/${postId}`}
                rel="noreferrer"
                target="_blank"
              >
                Original post on X
              </a>
            </>
          ) : (
            <p className="text-muted-foreground text-sm">{fallbackTitle}</p>
          )}
          <div className="text-muted-foreground mt-4 flex items-center gap-2 text-xs">
            {embedState === "failed" ? (
              <CircleAlert className="size-4 shrink-0" />
            ) : (
              <LoaderCircle
                className={cn(
                  "size-4 shrink-0",
                  nearViewport && "animate-spin",
                )}
              />
            )}
            <p>
              {embedState === "failed"
                ? "Couldn’t load the X embed. Try again or open the original."
                : nearViewport
                  ? "Loading the original post from X…"
                  : "Post loads as you scroll here."}
            </p>
          </div>
          {embedState === "failed" && (
            <button
              className="mt-3 flex items-center gap-1 rounded-lg border px-3 py-2 text-xs"
              type="button"
              onClick={() => {
                setEmbedState("loading");
                setAttempt((value) => value + 1);
              }}
            >
              <RotateCw className="size-3" />
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Load the provider library once, sharing the result across every post card.
//
function loadXWidgets(): Promise<XWidgets> {
  if (window.twttr?.widgets) return Promise.resolve(window.twttr.widgets);
  if (xWidgetsPromise) return xWidgetsPromise;

  xWidgetsPromise = new Promise<XWidgets>((resolve, reject) => {
    const platform = getQueuedXPlatform();
    const existingScript = document.getElementById(
      X_WIDGET_SCRIPT_ID,
    ) as HTMLScriptElement | null;
    const script = existingScript ?? document.createElement("script");
    let isSettled = false;

    // Release listeners and the timeout as soon as loading has a final state.
    //
    function cleanUp() {
      window.clearTimeout(timeoutId);
      script.removeEventListener("error", handleError);
      script.removeEventListener("load", handleLoad);
    }

    // Resolve only after widgets.js exposes the factory used by the card.
    //
    function handleReady(readyPlatform: XPlatform) {
      const widgets = readyPlatform.widgets ?? window.twttr?.widgets;
      if (!widgets || isSettled) return;

      isSettled = true;
      cleanUp();
      resolve(widgets);
    }

    // A loaded script should normally have flushed the ready queue; keep this
    // direct check for implementations that expose widgets synchronously.
    //
    function handleLoad() {
      if (window.twttr?.widgets) handleReady(window.twttr);
    }

    // Remove a failed provider script so a later route or theme change can
    // retry instead of retaining a permanently rejected global promise.
    //
    function handleError() {
      if (isSettled) return;

      isSettled = true;
      cleanUp();
      script.remove();
      if (!window.twttr?.widgets) delete window.twttr;
      xWidgetsPromise = null;
      reject(new Error("X's post renderer could not be loaded."));
    }

    const timeoutId = window.setTimeout(handleError, X_WIDGET_TIMEOUT_MS);
    platform.ready(handleReady);
    script.addEventListener("error", handleError, { once: true });
    script.addEventListener("load", handleLoad, { once: true });

    if (!existingScript) {
      script.async = true;
      script.id = X_WIDGET_SCRIPT_ID;
      script.src = X_WIDGET_SCRIPT_URL;
      document.head.append(script);
    }
  });

  return xWidgetsPromise;
}

// Mirror X's documented pre-load queue so callers can register before the
// asynchronous provider script has finished evaluating.
//
function getQueuedXPlatform(): XPlatform {
  if (window.twttr) return window.twttr;

  const callbacks: XReadyCallback[] = [];
  const platform: XPlatform = {
    _e: callbacks,
    ready: (callback) => callbacks.push(callback),
  };
  window.twttr = platform;
  return platform;
}
